/**
 * Workflow Execution Service
 * Triggers and executes automation workflows based on entity events
 */

import { supabase } from '../supabase';
import { coreCall } from './gatewayService';
import { getUserData } from '../utils';
import type {
  WorkflowTriggerType,
  WorkflowEntityType,
  AutomationWorkflow,
  AutomationWorkflowStep,
  AutomationWorkflowEntity,
  StepExecutionContext,
  StepExecutionResult,
} from '../types/workflow';
import {
  executeCreateActivity,
  executeCreateNote,
  executeStateChange,
  executeSendNotification,
  executeWait,
  executeCondition,
  executeSplit,
} from './workflow-handlers';

interface TriggerWorkflowsParams {
  entityType: WorkflowEntityType;
  triggerType: WorkflowTriggerType;
  entityId: string;
  triggerData?: Record<string, any>;
  triggerConfig?: Record<string, any>; // Additional filters (pipeline_id, from_state, to_state, etc.)
}

class WorkflowExecutionService {
  /**
   * Main entry point: triggers all matching workflows for an entity event
   */
  async triggerWorkflows(params: TriggerWorkflowsParams): Promise<void> {
    const { entityType, triggerType, entityId, triggerData = {}, triggerConfig = {} } = params;
    const startTime = Date.now();

    try {
      console.log('🔄 [WORKFLOW] Triggering workflows:', {
        entityType,
        triggerType,
        entityId,
        triggerConfig,
      });

      // Get current user's organization
      const userData = await getUserData();
      if (!userData?.organizationId) {
        console.warn('⚠️ [WORKFLOW] No organization found, skipping workflow execution');

        // Log failed trigger attempt
        await this.logTrigger({
          triggerType,
          entityId,
          entityType,
          organizationId: 'unknown',
          triggerSource: 'client',
          triggerData,
          workflowsFound: 0,
          workflowsTriggered: 0,
          entitiesEnrolled: 0,
          success: false,
          errorMessage: 'No organization found',
          executionTimeMs: Date.now() - startTime
        });

        return;
      }

      // Find all active workflows matching this trigger
      const matchingWorkflows = await this.findMatchingWorkflows(
        userData.organizationId,
        entityType,
        triggerType,
        triggerConfig
      );

      const workflowsFound = matchingWorkflows.length;

      if (workflowsFound === 0) {
        console.log('ℹ️ [WORKFLOW] No matching workflows found');

        // Log trigger with no workflows found
        await this.logTrigger({
          triggerType,
          entityId,
          entityType,
          organizationId: userData.organizationId,
          triggerSource: 'client',
          triggerData,
          workflowsFound: 0,
          workflowsTriggered: 0,
          entitiesEnrolled: 0,
          success: true,
          executionTimeMs: Date.now() - startTime
        });

        return;
      }

      console.log(`✅ [WORKFLOW] Found ${workflowsFound} matching workflow(s)`);

      // Enroll entity in each matching workflow
      let workflowsTriggered = 0;
      let entitiesEnrolled = 0;

      for (const workflow of matchingWorkflows) {
        const enrolled = await this.enrollEntityInWorkflow(workflow, entityId, triggerData, userData.organizationId);
        if (enrolled) {
          workflowsTriggered++;
          entitiesEnrolled++;
        }
      }

      // Log successful trigger
      await this.logTrigger({
        triggerType,
        entityId,
        entityType,
        organizationId: userData.organizationId,
        triggerSource: 'client',
        triggerData,
        workflowsFound,
        workflowsTriggered,
        entitiesEnrolled,
        success: true,
        executionTimeMs: Date.now() - startTime
      });

      console.log(`✅ [WORKFLOW] Triggered ${workflowsTriggered}/${workflowsFound} workflows in ${Date.now() - startTime}ms`);

    } catch (error) {
      console.error('❌ [WORKFLOW] Error triggering workflows:', error);

      // Log failed trigger
      try {
        const userData = await getUserData();
        await this.logTrigger({
          triggerType,
          entityId,
          entityType,
          organizationId: userData?.organizationId || 'unknown',
          triggerSource: 'client',
          triggerData,
          workflowsFound: 0,
          workflowsTriggered: 0,
          entitiesEnrolled: 0,
          success: false,
          errorMessage: (error as Error).message,
          executionTimeMs: Date.now() - startTime
        });
      } catch (logError) {
        console.error('Failed to log workflow trigger error:', logError);
      }
    }
  }

  /**
   * Find workflows that match the trigger criteria
   */
  private async findMatchingWorkflows(
    organizationId: string,
    entityType: WorkflowEntityType,
    triggerType: WorkflowTriggerType,
    triggerConfig: Record<string, any>
  ): Promise<AutomationWorkflow[]> {
    // Query active workflows with matching entity type and trigger type
    const { data: workflows, error } = await supabase
      .from('automation_workflows')
      .select('*')
      .eq('organization_id', organizationId)
      .eq('entity_type', entityType)
      .eq('trigger_type', triggerType)
      .eq('is_active', true)
      .in('status', ['active', 'testing']);

    if (error) {
      console.error('❌ [WORKFLOW] Error fetching workflows:', error);
      return [];
    }

    if (!workflows || workflows.length === 0) {
      return [];
    }

    // Filter workflows by trigger_config conditions + testing mode whitelist
    const matchingWorkflows = workflows.filter((workflow) => {
      // Testing mode: only fire for whitelisted entities
      if (workflow.status === 'testing') {
        const testIds = workflow.test_entity_ids || [];
        if (!testIds.includes(entityId)) {
          console.log(`⏭️ [WORKFLOW] Skipping testing workflow "${workflow.name}" — entity ${entityId} not in whitelist`);
          return false;
        }
      }
      return this.matchesTriggerConfig(workflow, triggerConfig);
    });

    return matchingWorkflows as AutomationWorkflow[];
  }

  /**
   * Check if workflow's trigger_config matches the event's triggerConfig
   */
  private matchesTriggerConfig(
    workflow: AutomationWorkflow,
    triggerConfig: Record<string, any>
  ): boolean {
    const workflowConfig = workflow.trigger_config || {};

    // If workflow has no filters, it matches all events
    if (Object.keys(workflowConfig).length === 0) {
      return true;
    }

    // Check each configured filter
    for (const [key, expectedValue] of Object.entries(workflowConfig)) {
      // Skip empty/null filters (they mean "any")
      if (expectedValue === null || expectedValue === undefined || expectedValue === '') {
        continue;
      }

      // Skip _position if it's an object (workflow builder coordinates, not a filter)
      if (key === '_position' && typeof expectedValue === 'object' && !Array.isArray(expectedValue)) {
        continue;
      }

      // Impostazioni del workflow, non condizioni sul lead: se entrassero nel confronto
      // diventerebbero filtri che nessun lead soddisfa e il workflow non partirebbe piu'.
      // Deve restare allineato a NON_FILTER_KEYS in supabase/functions/trigger-workflow.
      if (key === 'allow_reenrollment') {
        continue;
      }

      let actualValue = triggerConfig[key];

      // Normalize pipeline fields: treat null/undefined as "legacy" (default pipeline)
      // The UI now uses 'legacy' string explicitly, so we only need to normalize null/undefined
      const isPipelineField = key === 'pipeline_id' || key === 'from_pipeline_id' || key === 'to_pipeline_id';
      let normalizedExpected = expectedValue;
      let normalizedActual = actualValue;

      if (isPipelineField && !Array.isArray(expectedValue)) {
        normalizedExpected = (expectedValue === null || expectedValue === undefined) ? 'legacy' : expectedValue;
        normalizedActual = (actualValue === null || actualValue === undefined) ? 'legacy' : actualValue;
      }

      // Handle array filters (multiselect fields)
      if (Array.isArray(expectedValue)) {
        // If expected is empty array, match any
        if (expectedValue.length === 0) continue;

        // For pipeline fields in arrays, normalize null/undefined to "legacy"
        const valuesToCheck = isPipelineField
          ? expectedValue.map(v => (v === null || v === undefined) ? 'legacy' : v)
          : expectedValue;

        // Handle array-to-array comparison (e.g., interest_category_ids)
        // Check if there's at least one common element
        if (Array.isArray(actualValue)) {
          // If actual is empty array, no match
          if (actualValue.length === 0) {
            console.log(`⚠️ [WORKFLOW] Filter mismatch on ${key}: expected one of [${valuesToCheck.join(', ')}], got empty array`);
            return false;
          }
          // Check intersection - at least one common element
          const hasIntersection = valuesToCheck.some(v => actualValue.includes(v));
          if (!hasIntersection) {
            console.log(`⚠️ [WORKFLOW] Filter mismatch on ${key}: expected one of [${valuesToCheck.join(', ')}], got [${actualValue.join(', ')}] (no intersection)`);
            return false;
          }
        } else {
          // actualValue is a single value, check if it's in the expected array
          const actualToCheck = isPipelineField && (actualValue === null || actualValue === undefined) ? 'legacy' : actualValue;

          // Check if actual value is in the expected array
          if (!valuesToCheck.includes(actualToCheck)) {
            console.log(`⚠️ [WORKFLOW] Filter mismatch on ${key}: expected one of [${valuesToCheck.join(', ')}], got ${actualToCheck}`);
            return false;
          }
        }
      } else {
        // Simple equality check with normalization
        if (normalizedActual !== normalizedExpected) {
          console.log(`⚠️ [WORKFLOW] Filter mismatch on ${key}: expected ${normalizedExpected}, got ${normalizedActual}`);
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Enroll an entity in a workflow (start the workflow)
   * NEW: Creates a 'pending' execution and triggers backend processing via RPC
   * @returns true if entity was enrolled, false if already enrolled or error occurred
   */
  private async enrollEntityInWorkflow(
    workflow: AutomationWorkflow,
    entityId: string,
    triggerData: Record<string, any>,
    organizationId: string
  ): Promise<boolean> {
    try {
      console.log(`🚀 [WORKFLOW] Enrolling entity ${entityId} in workflow "${workflow.name}"`);

      // Check if entity is already enrolled in this workflow
      // Handle multiple enrollments by ordering by most recent first
      const { data: existingEnrollments, error: checkError } = await supabase
        .from('automation_workflow_entities')
        .select('id, status, entered_at')
        .eq('workflow_id', workflow.id)
        .eq('entity_id', entityId)
        .eq('organization_id', organizationId)
        .order('entered_at', { ascending: false });

      if (checkError) {
        console.error('❌ [WORKFLOW] Error checking enrollment:', checkError);
        return false;
      }

      // Get the most recent enrollment (if multiple exist due to duplicates)
      const existingEnrollment = existingEnrollments?.[0];

      // If already enrolled and active/pending, skip
      if (existingEnrollment && (existingEnrollment.status === 'active' || existingEnrollment.status === 'pending')) {
        console.log(`ℹ️ [WORKFLOW] Entity already enrolled in workflow (status: ${existingEnrollment.status})`);
        return false;
      }

      // Get first step of workflow
      const { data: firstStep, error: stepError } = await supabase
        .from('automation_workflow_steps')
        .select('id')
        .eq('workflow_id', workflow.id)
        .eq('organization_id', organizationId)
        .order('step_order', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (stepError || !firstStep) {
        console.error('❌ [WORKFLOW] No steps found for workflow:', workflow.name);
        return false;
      }

      // Get total step count
      const { count: totalSteps } = await supabase
        .from('automation_workflow_steps')
        .select('id', { count: 'exact', head: true })
        .eq('workflow_id', workflow.id)
        .eq('organization_id', organizationId);

      // Create enrollment record with 'pending' status
      // The backend Edge Function will process it
      const { data: enrollment, error: enrollError } = await supabase
        .from('automation_workflow_entities')
        .insert({
          workflow_id: workflow.id,
          entity_id: entityId,
          entity_type: workflow.entity_type,
          organization_id: organizationId,
          status: 'pending', // ✨ NEW: Start as pending instead of active
          current_step_id: firstStep.id,
          entered_at: new Date().toISOString(),
          next_action_at: new Date().toISOString(), // Execute immediately
          steps_completed: 0,
          total_steps: totalSteps || 0,
          trigger_data: triggerData,
        })
        .select()
        .single();

      if (enrollError) {
        console.error('❌ [WORKFLOW] Error creating enrollment:', enrollError);
        return false;
      }

      console.log(`✅ [WORKFLOW] Entity enrolled successfully in "${workflow.name}" (status: pending)`);

      // Stats update fire-and-forget: non bloccare l'enrollment
      void this.incrementWorkflowStat(workflow.id, 'triggered_count');
      void this.incrementWorkflowStat(workflow.id, 'active_entities');

      // Real-time tickle del worker backend, fire-and-forget.
      // Il chiamante prosegue subito; il processing parte in background.
      // Fallback: cron BullMQ ogni 30s pesca comunque pending entities.
      this.triggerWorkflowProcessing(enrollment.id);

      return true;
    } catch (error) {
      console.error('❌ [WORKFLOW] Error enrolling entity:', error);
      return false;
    }
  }

  /**
   * Tickle real-time del worker backend per processare l'enrollment appena creato.
   * Fire-and-forget: nessun await dal chiamante, errori loggati ma non propagati.
   * Il backend risponde 202 immediatamente (async mode), batch parte in background.
   */
  private triggerWorkflowProcessing(entityId: string): void {
    console.log(`⚡ [WORKFLOW] Triggering real-time processing for entity: ${entityId}`);
    coreCall('workflows/process', {
      body: { batch_size: 1, async: true },
    })
      .then(({ error }) => {
        if (error) {
          console.warn(
            `⚠️ [WORKFLOW] Backend kick failed (cron will pick up):`,
            error.message,
          );
        }
      })
      .catch((err) => {
        console.warn(`⚠️ [WORKFLOW] Backend kick exception:`, err);
      });
  }

  /**
   * Execute the next step for an enrolled entity
   */
  private async executeNextStep(enrollment: AutomationWorkflowEntity): Promise<void> {
    try {
      if (!enrollment.current_step_id) {
        console.log('✅ [WORKFLOW] Workflow completed - no more steps');
        await this.completeWorkflowEnrollment(enrollment.id);
        return;
      }

      console.log(`🔄 [WORKFLOW] Executing step ${enrollment.current_step_id}`);

      // Get step details
      const { data: step, error: stepError } = await supabase
        .from('automation_workflow_steps')
        .select('*')
        .eq('id', enrollment.current_step_id)
        .single();

      if (stepError || !step) {
        console.error('❌ [WORKFLOW] Step not found:', enrollment.current_step_id);
        await this.failWorkflowEnrollment(enrollment.id, 'Step not found');
        return;
      }

      console.log(`📋 [WORKFLOW] Step type: ${step.step_type}, name: ${step.name}`);

      // Increment step stats
      await this.incrementStepStat(step.id, 'entered_count');

      // Get workflow details
      const { data: workflow, error: workflowError } = await supabase
        .from('automation_workflows')
        .select('*')
        .eq('id', enrollment.workflow_id)
        .single();

      if (workflowError || !workflow) {
        console.error('❌ [WORKFLOW] Workflow not found');
        await this.failWorkflowEnrollment(enrollment.id, 'Workflow not found');
        return;
      }

      // Get entity data for step execution
      const entityData = await this.getEntityData(
        enrollment.entity_id,
        enrollment.entity_type,
        enrollment.organization_id
      );

      if (!entityData) {
        console.error('❌ [WORKFLOW] Entity not found');
        await this.failWorkflowEnrollment(enrollment.id, 'Entity not found');
        return;
      }

      // Get user data
      const userData = await getUserData();

      // Build execution context
      const context: StepExecutionContext = {
        workflow: workflow as AutomationWorkflow,
        step: step as AutomationWorkflowStep,
        entity: enrollment,
        entityData,
        organizationId: enrollment.organization_id,
        userId: userData?.userId,
      };

      // Execute step based on type
      let result: StepExecutionResult;

      switch (step.step_type) {
        case 'create_activity':
          result = await executeCreateActivity(context);
          break;
        case 'create_note':
          result = await executeCreateNote(context);
          break;
        case 'state_change':
          result = await executeStateChange(context);
          break;
        case 'send_notification':
          result = await executeSendNotification(context);
          break;
        case 'wait':
          result = await executeWait(context);
          break;
        case 'condition':
          result = await executeCondition(context);
          break;
        case 'split':
          result = await executeSplit(context);
          break;
        default:
          console.warn(`⚠️ [WORKFLOW] Unsupported step type: ${step.step_type}`);
          result = {
            success: false,
            error: `Unsupported step type: ${step.step_type}`,
          };
      }

      // Handle execution result
      if (!result.success) {
        console.error(`❌ [WORKFLOW] Step execution failed: ${result.error}`);
        await this.incrementStepStat(step.id, 'failed_count');
        await this.failWorkflowEnrollment(enrollment.id, result.error || 'Step execution failed');

        // Log execution error
        await this.logExecution(enrollment, step, 'failed', result);
        return;
      }

      // Log successful execution
      await this.logExecution(enrollment, step, 'success', result);
      await this.incrementStepStat(step.id, 'completed_count');

      // Handle wait step
      if (result.shouldWait && result.waitUntil) {
        console.log(`⏰ [WORKFLOW] Pausing until ${result.waitUntil.toISOString()}`);

        await supabase
          .from('automation_workflow_entities')
          .update({
            current_step_id: result.nextStepId,
            next_action_at: result.waitUntil.toISOString(),
            steps_completed: enrollment.steps_completed + 1,
          })
          .eq('id', enrollment.id);

        return; // Don't proceed to next step yet
      }

      // Move to next step
      const nextStepId = result.nextStepId;

      await supabase
        .from('automation_workflow_entities')
        .update({
          current_step_id: nextStepId,
          steps_completed: enrollment.steps_completed + 1,
          next_action_at: new Date().toISOString(), // Execute next step immediately
        })
        .eq('id', enrollment.id);

      console.log(`✅ [WORKFLOW] Step completed, moving to next: ${nextStepId || 'END'}`);

      // If no next step, complete workflow
      if (!nextStepId) {
        await this.completeWorkflowEnrollment(enrollment.id);
      } else {
        // Continue with next step immediately (recursive call)
        const { data: updatedEnrollment } = await supabase
          .from('automation_workflow_entities')
          .select('*')
          .eq('id', enrollment.id)
          .single();

        if (updatedEnrollment) {
          await this.executeNextStep(updatedEnrollment as AutomationWorkflowEntity);
        }
      }
    } catch (error) {
      console.error('❌ [WORKFLOW] Error executing step:', error);
      await this.failWorkflowEnrollment(enrollment.id, (error as Error).message);
    }
  }

  /**
   * Get entity data for execution context
   */
  private async getEntityData(
    entityId: string,
    entityType: WorkflowEntityType,
    organizationId: string
  ): Promise<Record<string, any> | null> {
    let tableName: string;

    switch (entityType) {
      case 'lead':
      case 'client':
      case 'newsletter': // Newsletter workflows use client/lead as entity
        tableName = 'clients';
        break;
      case 'deal':
        tableName = 'deals';
        break;
      default:
        return null;
    }

    const { data, error } = await supabase
      .from(tableName)
      .select('*')
      .eq('id', entityId)
      .eq('organization_id', organizationId)
      .single();

    if (error) {
      console.error(`Error fetching ${entityType} data:`, error);
      return null;
    }

    return data;
  }

  /**
   * Mark workflow enrollment as failed
   */
  private async failWorkflowEnrollment(enrollmentId: string, reason: string): Promise<void> {
    await supabase
      .from('automation_workflow_entities')
      .update({
        status: 'failed',
        exit_reason: reason,
        exited_at: new Date().toISOString(),
      })
      .eq('id', enrollmentId);

    // Update workflow stats
    const { data: enrollment } = await supabase
      .from('automation_workflow_entities')
      .select('workflow_id')
      .eq('id', enrollmentId)
      .single();

    if (enrollment) {
      await this.decrementWorkflowStat(enrollment.workflow_id, 'active_entities');
    }

    console.log(`❌ [WORKFLOW] Workflow enrollment failed: ${reason}`);
  }

  /**
   * Log step execution
   */
  private async logExecution(
    enrollment: AutomationWorkflowEntity,
    step: AutomationWorkflowStep,
    status: 'success' | 'failed' | 'skipped',
    result: StepExecutionResult
  ): Promise<void> {
    try {
      // Tabella al SINGOLARE: `automation_execution_log`. Col plurale PostgREST
      // rispondeva 404 e nessuno step ha mai lasciato traccia di esecuzione.
      await supabase.from('automation_execution_log').insert({
        workflow_id: enrollment.workflow_id,
        workflow_entity_id: enrollment.id,
        step_id: step.id,
        action: `${step.step_type}: ${step.name}`,
        status,
        result_data: result.data || null,
        error_message: result.error || null,
        executed_at: new Date().toISOString(),
      });
    } catch (error) {
      console.error('Error logging execution:', error);
    }
  }

  /**
   * Mark workflow enrollment as completed
   */
  private async completeWorkflowEnrollment(enrollmentId: string): Promise<void> {
    await supabase
      .from('automation_workflow_entities')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
      })
      .eq('id', enrollmentId);

    // Update workflow stats
    const { data: enrollment } = await supabase
      .from('automation_workflow_entities')
      .select('workflow_id')
      .eq('id', enrollmentId)
      .single();

    if (enrollment) {
      await this.incrementWorkflowStat(enrollment.workflow_id, 'completed_count');
      await this.decrementWorkflowStat(enrollment.workflow_id, 'active_entities');
    }

    console.log('🎉 [WORKFLOW] Workflow enrollment completed!');
  }

  /**
   * Increment workflow statistics
   */
  private async incrementWorkflowStat(workflowId: string, statField: string): Promise<void> {
    try {
      await supabase.rpc('increment_workflow_stat', {
        p_workflow_id: workflowId,
        p_stat_name: statField,
      });
    } catch (err) {
      // If function doesn't exist, fallback to manual increment
      console.warn(`⚠️ [WORKFLOW] RPC increment failed, using fallback for ${statField}`);
    }
  }

  /**
   * Decrement workflow statistics
   */
  private async decrementWorkflowStat(workflowId: string, statField: string): Promise<void> {
    try {
      await supabase.rpc('decrement_workflow_stat', {
        p_workflow_id: workflowId,
        p_stat_name: statField,
      });
    } catch (err) {
      console.warn(`⚠️ [WORKFLOW] RPC decrement failed for ${statField}`);
    }
  }

  /**
   * Increment step statistics
   */
  private async incrementStepStat(stepId: string, statField: string): Promise<void> {
    try {
      await supabase.rpc('increment_step_stat', {
        p_step_id: stepId,
        p_stat_name: statField,
      });
    } catch (err) {
      console.warn(`⚠️ [WORKFLOW] RPC increment failed for step ${statField}`);
    }
  }

  /**
   * Convenience methods for common triggers
   */

  async onLeadCreated(leadId: string, pipelineId?: string): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'lead',
      triggerType: 'lead_created',
      entityId: leadId,
      triggerConfig: { pipeline_id: pipelineId },
    });
  }

  async onLeadStateChanged(
    leadId: string,
    pipelineId: string,
    fromStateId?: string,
    toStateId?: string
  ): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'lead',
      triggerType: 'lead_state_changed',
      entityId: leadId,
      triggerConfig: {
        pipeline_id: pipelineId,
        from_state_id: fromStateId,
        to_state_id: toStateId,
      },
    });
  }

  async onLeadPipelineChanged(
    leadId: string,
    fromPipelineId: string,
    toPipelineId: string
  ): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'lead',
      triggerType: 'lead_pipeline_changed',
      entityId: leadId,
      triggerConfig: {
        from_pipeline_id: fromPipelineId,
        to_pipeline_id: toPipelineId,
      },
    });
  }

  async onLeadUpdated(leadId: string, pipelineId?: string, fieldsChanged?: string[]): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'lead',
      triggerType: 'lead_updated',
      entityId: leadId,
      triggerConfig: {
        pipeline_id: pipelineId,
        fields_changed: fieldsChanged,
      },
    });
  }

  async onLeadConvertedToClient(leadId: string, clientId: string): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'lead',
      triggerType: 'lead_converted_to_client',
      entityId: leadId,
      triggerData: { clientId },
    });
  }

  async onLeadNoteCreated(leadId: string, noteId: string, pipelineId?: string): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'lead',
      triggerType: 'lead_note_created',
      entityId: leadId,
      triggerConfig: { pipeline_id: pipelineId },
      triggerData: { noteId },
    });
  }

  async onLeadActivityCreated(
    leadId: string,
    activityId: string,
    activityType: string,
    pipelineId?: string,
    activityData?: {
      title?: string;
      dueDate?: string;
      assignedTo?: string;
      businessName?: string;
    }
  ): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'lead',
      triggerType: 'lead_activity_created',
      entityId: leadId,
      triggerConfig: {
        pipeline_id: pipelineId,
        activity_type: activityType,
      },
      triggerData: {
        activity_id: activityId,
        activity_title: activityData?.title,
        activity_type: activityType,
        activity_date: activityData?.dueDate,
        assigned_to: activityData?.assignedTo,
        business_name: activityData?.businessName,
      },
    });
  }

  async onClientCreated(clientId: string): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'client',
      triggerType: 'client_created',
      entityId: clientId,
    });
  }

  async onClientUpdated(clientId: string, fieldsChanged?: string[]): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'client',
      triggerType: 'client_updated',
      entityId: clientId,
      triggerConfig: { fields_changed: fieldsChanged },
    });
  }

  async onClientNoteCreated(clientId: string, noteId: string): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'client',
      triggerType: 'client_note_created',
      entityId: clientId,
      triggerData: { noteId },
    });
  }

  async onClientActivityCreated(
    clientId: string,
    activityId: string,
    activityType: string,
    activityData?: {
      title?: string;
      dueDate?: string;
      assignedTo?: string;
      businessName?: string;
    }
  ): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'client',
      triggerType: 'client_activity_created',
      entityId: clientId,
      triggerConfig: { activity_type: activityType },
      triggerData: {
        activity_id: activityId,
        activity_title: activityData?.title,
        activity_type: activityType,
        activity_date: activityData?.dueDate,
        assigned_to: activityData?.assignedTo,
        business_name: activityData?.businessName,
      },
    });
  }

  async onDealCreated(dealId: string): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'deal',
      triggerType: 'deal_created',
      entityId: dealId,
    });
  }

  async onDealStateChanged(dealId: string, fromStatus?: string, toStatus?: string): Promise<void> {
    await this.triggerWorkflows({
      entityType: 'deal',
      triggerType: 'deal_state_changed',
      entityId: dealId,
      triggerConfig: {
        from_status: fromStatus,
        to_status: toStatus,
      },
    });
  }

  /**
   * Process pending workflows (for scheduled execution)
   * Should be called periodically (e.g., every minute via cron job)
   */
  async processPendingWorkflows(): Promise<void> {
    try {
      console.log('🔄 [WORKFLOW SCHEDULER] Processing pending workflows...');

      // Get all active enrollments that are ready to execute
      const { data: pendingEnrollments, error } = await supabase
        .from('automation_workflow_entities')
        .select('*')
        .eq('status', 'active')
        .not('current_step_id', 'is', null)
        .lte('next_action_at', new Date().toISOString())
        .limit(50); // Process max 50 at a time

      if (error) {
        console.error('❌ [WORKFLOW SCHEDULER] Error fetching pending enrollments:', error);
        return;
      }

      if (!pendingEnrollments || pendingEnrollments.length === 0) {
        console.log('ℹ️ [WORKFLOW SCHEDULER] No pending workflows to process');
        return;
      }

      console.log(`✅ [WORKFLOW SCHEDULER] Found ${pendingEnrollments.length} pending workflow(s)`);

      // Process each enrollment
      for (const enrollment of pendingEnrollments) {
        try {
          await this.executeNextStep(enrollment as AutomationWorkflowEntity);
        } catch (error) {
          console.error(`❌ [WORKFLOW SCHEDULER] Error processing enrollment ${enrollment.id}:`, error);
        }
      }

      console.log('✅ [WORKFLOW SCHEDULER] Finished processing pending workflows');
    } catch (error) {
      console.error('❌ [WORKFLOW SCHEDULER] Error in scheduler:', error);
    }
  }

  /**
   * Log workflow trigger execution to database for monitoring and analytics
   */
  private async logTrigger(params: {
    triggerType: string;
    entityId: string;
    entityType: string;
    organizationId: string;
    triggerSource: string;
    triggerData: Record<string, any>;
    workflowsFound: number;
    workflowsTriggered: number;
    entitiesEnrolled: number;
    success: boolean;
    errorMessage?: string;
    executionTimeMs: number;
  }): Promise<void> {
    try {
      const { error } = await supabase.rpc('log_workflow_trigger', {
        p_trigger_type: params.triggerType,
        p_entity_id: params.entityId,
        p_entity_type: params.entityType,
        p_organization_id: params.organizationId,
        p_trigger_source: params.triggerSource,
        p_trigger_data: params.triggerData,
        p_workflows_found: params.workflowsFound,
        p_workflows_triggered: params.workflowsTriggered,
        p_entities_enrolled: params.entitiesEnrolled,
        p_success: params.success,
        p_error_message: params.errorMessage || null,
        p_execution_time_ms: params.executionTimeMs
      });

      if (error) {
        console.error('Failed to log workflow trigger:', error);
      }
    } catch (error) {
      console.error('Failed to log workflow trigger:', error);
    }
  }
}

export const workflowExecutionService = new WorkflowExecutionService();
